import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Header, Grid, Popup, Icon } from 'semantic-ui-react';
import { Line } from 'react-chartjs-2';
import { Message } from 'components';
import { colors, graph } from 'utils/colors';
import { Planning } from 'actions';
import TaxImplications from './TaxImplications';

const SAVING_OPTIONS = [
  {
    key: '0.5',
    value: 0.5,
    label: '50%',
    accent: '#b8c4ff',
    border: '#8e9df0',
  },
  {
    key: '0.75',
    value: 0.75,
    label: '75%',
    accent: '#6b8cff',
    border: '#6b7be8',
  },
  {
    key: '0.9',
    value: 0.9,
    label: '90%',
    accent: '#1f2e86',
    border: '#1f2e86',
  },
];

const GAUGE_RADIUS = 48;
const GAUGE_LENGTH = Math.PI * GAUGE_RADIUS;

function Gauge({ value, label, accent }) {
  return (
    <GaugeWrapper>
      <svg viewBox="0 0 120 60" width="100%" role="img" aria-label={label}>
        <path
          d="M 12 60 A 48 48 0 0 1 108 60"
          fill="none"
          stroke="#eeeeee"
          strokeWidth="14"
        />
        <path
          d="M 12 60 A 48 48 0 0 1 108 60"
          fill="none"
          stroke={accent}
          strokeWidth="14"
          strokeDasharray={`${value * GAUGE_LENGTH} ${GAUGE_LENGTH}`}
        />
        <text
          x="60"
          y="60"
          textAnchor="middle"
          fontSize="19"
          fontWeight="700"
          fill="#1f2e86"
        >
          {label}
        </text>
      </svg>
    </GaugeWrapper>
  );
}

Gauge.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
};

class Result extends Component {
  state = { maxOfChart: null };

  componentDidUpdate(prevProps) {
    if (prevProps.result !== this.props.result)
      this.setState({
        maxOfChart: null,
      });
  }

  componentWillUnmount() {
    const { history, result } = this.props;

    if (history.location.pathname.includes('target_wealth') && result) {
      localStorage.setItem('cacheSR', JSON.stringify(result));
    }
  }

  getChartData = (paths) => {
    if (paths) {
      const { current, high, low, min } = paths;
      const dataLength = Object.values(current).length;
      if (!min) {
        // Old blueprints will break if they do not have min data
        return false;
      }

      return {
        labels: new Array(Object.keys(current).length)
          .fill(0)
          .map((n, index) => Object.keys(current).length - index - 1),
        datasets: [
          {
            data: Object.values(min),
            label: '',
            lineTension: 0,
            fill: false,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            pointBackgroundColor: 'transparent',
            pointBorderColor: 'transparent',
          },
          {
            data: Object.values(low),
            borderWidth: 3,
            label: 'Lowest to 5th Percentile',
            lineTension: 0,
            fill: 0,
            backgroundColor: graph.teal,
            borderColor: graph.teal,
            pointRadius: 0,
            pointBackgroundColor: graph.teal,
            pointBorderColor: graph.teal,
            pointHitRadius: 5,
          },
          {
            data: Object.values(current),
            borderWidth: 3,
            label: '5th Percentile to Meets Goal',
            lineTension: 0,
            fill: 0,
            backgroundColor: graph.darkBlue,
            borderColor: graph.darkBlue,
            pointRadius: Array(dataLength - 1)
              .fill(0)
              .concat([5]),
            pointBackgroundColor: Array(dataLength - 1)
              .fill(graph.darkBlue)
              .concat([colors.red]),
            pointBorderColor: Array(dataLength - 1)
              .fill(graph.darkBlue)
              .concat([colors.red]),
            pointHitRadius: 5,
          },
          {
            data: Object.values(high),
            borderWidth: 3,
            label: 'Meets Goal to 95th Percentile',
            lineTension: 0,
            fill: 0,
            backgroundColor: graph.lightBlue,
            borderColor: graph.lightBlue,
            pointRadius: 0,
            pointBackgroundColor: graph.lightBlue,
            pointBorderColor: graph.lightBlue,
            pointHitRadius: 5,
          },
        ],
      };
    }

    return false;
  };

  setChartMax = (label, index, values) => {
    const { maxOfChart } = this.state;

    if (values[0] > maxOfChart) {
      this.setState({ maxOfChart: values[0] });
    }
  };

  nextStep = () => {
    const { history, dispatch, match } = this.props;

    dispatch(
      Planning.updateBlueprintData('summary', { id: Number(match.params.id) })
    );
    dispatch(
      Planning.updateBlueprintData('summary', { id: Number(match.params.id) })
    );
    dispatch(Planning.nextSubPage('targetSaving'));
    dispatch(Planning.updateBlueprintData('summary', { currentSubPage: 1 }));
    return history.push(`/planning/blueprint/${match.params.id}/summary`);
  };

  updateMonthlySavingChosen = (value) => {
    const { dispatch } = this.props;
    dispatch(
      Planning.updateBlueprintData('targetSaving', {
        monthlySavingChosen: value,
      })
    );
    if (!localStorage.getItem('cacheMSChanged')) {
      localStorage.setItem('cacheMSChanged', true);
    }
    this.scrollToBottom();
  };

  scrollToBottom = () => this.scrollHandler(document.body.scrollHeight, 500);

  scrollHandler = (to, duration) => {
    if (duration <= 0) return false;
    const el = document.scrollingElement || document.documentElement;
    const difference = to - el.scrollTop;
    const perTick = (difference / duration) * 10;

    return setTimeout(() => {
      el.scrollTop += perTick;
      if (el.scrollTop === to) {
        return false;
      }
      return this.scrollHandler(to, duration - 10);
    }, 10);
  };

  displayYearMonth = (month) => {
    const year = month / 12;

    let str = '';

    if (year === 1) str = '1 year ';
    else str = `${year} years `;

    return (str += `(${month} months)`);
  };

  render() {
    const {
      result,
      isFetching,
      monthlySavingChosen,
      history,
      dispatch,
      match,
      isCouplePlan,
      startingAmount,
      yearsToTarget,
    } = this.props;
    const { maxOfChart } = this.state;
    const requiredSavings = !result ? null : result.prob_without_savings !== 1;
    const paths = result && result.paths ? result.paths : null;
    const chartOptions = {
      hover: {
        animationDuration: 0,
        onHover: (e) => {
          e.target.style.cursor = 'pointer';
        },
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: true,
              drawTicks: true,
              drawOnChartArea: false,
            },
            scaleLabel: {
              display: true,
              labelString: 'Months to target date',
              fontStyle: 'bold',
              fontSize: 16,
            },
            ticks: {
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
              callback(value, index, values) {
                const totalTicks = values.length;
                const step = Math.ceil(totalTicks / 20);
                return index % step === 0 || index === totalTicks - 1
                  ? value
                  : '';
              },
              fontSize: 14,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              callback: (label) => `$${label.toLocaleString()}`,
              max: maxOfChart,
              fontSize: 14,
            },
          },
        ],
      },
      tooltips: {
        callbacks: {
          title: (tooltipItem, data) => `${data.labels[tooltipItem[0].index]}`,
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex];

            if (tooltipItem.datasetIndex === 0) return null;
            const labelWords = dataset.label.split(' ');
            const lastTwoWords = labelWords.slice(-2).join(' ');
            return `${lastTwoWords}: $${Math.ceil(
              Number(tooltipItem.yLabel)
            ).toLocaleString()}`;
          },
          labelColor: (tooltipItem, chart) => {
            const dataset =
              chart.config.data.datasets[tooltipItem.datasetIndex];
            return {
              borderColor: dataset.borderColor,
              backgroundColor: dataset.backgroundColor,
            };
          },
        },
        mode: 'index',
        bodySpacing: 5,
      },
      legend: {
        position: 'top',
        labels: {
          filter(legendItem) {
            return legendItem.text !== '';
          },
        },
      },
    };
    const chartData = this.getChartData(paths[monthlySavingChosen]);
    const selectedPath = paths && paths[monthlySavingChosen];
    const goalPath = paths && paths[0.5];
    const selectedValues = selectedPath
      ? Object.values(selectedPath.current)
      : [];
    const goalValues = goalPath ? Object.values(goalPath.current) : [];
    const monthlySavings = Number(result[monthlySavingChosen]) || 0;
    const totalInvested =
      startingAmount + monthlySavings * selectedValues.length;
    const futureValueOfGoal = goalValues[goalValues.length - 1];
    const totalReturns = selectedValues.length
      ? selectedValues[selectedValues.length - 1] - totalInvested
      : 0;

    return !result ? null : (
      <div style={{ padding: '0 0 2rem' }}>
        <BlueprintContent style={{ opacity: isFetching ? 0.4 : 1 }}>
          <div
            style={{
              visibility: 'hidden',
              height: '0',
            }}
          >
            <Line
              setChartMax={this.setChartMax}
              data={() => {
                const dataSets = [];
                const values = Object.values(paths || {});

                values.forEach((v) => {
                  if (v) dataSets.push(...Object.values(v));
                });
                const temp = dataSets.map((d, index) => ({
                  data: Object.values(d),
                  borderWidth: 2,
                  label: index,
                  lineTension: 0,
                  fill: false,
                  backgroundColor: graph.red,
                  borderColor: graph.red,
                  pointBackgroundColor: graph.red,
                  pointBorderColor: graph.red,
                  pointRadius: 0,
                  pointHitRadius: 5,
                }));

                return {
                  datasets: temp,
                };
              }}
              options={{
                scales: {
                  xAxes: [
                    {
                      gridLines: {
                        display: false,
                      },
                    },
                  ],
                  yAxes: [
                    {
                      ticks: {
                        callback: (label, index, values) =>
                          this.setChartMax(label, index, values),
                      },
                    },
                  ],
                },
                tooltips: {
                  callbacks: {
                    title: (tooltipItem, data) =>
                      `${data.labels[tooltipItem[0].index]}`,
                    label: (tooltipItems, data) =>
                      `${
                        data.datasets[tooltipItems.datasetIndex].label
                      }: $${Math.ceil(
                        Number(tooltipItems.yLabel)
                      ).toLocaleString()}`,
                  },
                },
              }}
            />
          </div>

          <Header textAlign="left" size="large" style={{ maxWidth: '55rem' }}>
            How Much to Save Per Month
            <Header.Subheader style={{ margin: '.3rem auto .6rem' }}>
              Add this amount to your portfolio. Since the portfolio has
              uncertain outcomes, how much you need to save is associated with a
              probability. The probability represents the likelihood of reaching
              your goal by the target date. The results are generated based on
              10,000 simulations.
            </Header.Subheader>
          </Header>

          {!!requiredSavings ? (
            <Grid
              style={{
                position: 'relative',
                width: '100%',
                margin: '1rem auto',
              }}
            >
              <Grid.Row>
                <Grid.Column>
                  <Header
                    textAlign="center"
                    size="small"
                    style={{ maxWidth: '55rem', fontSize: '16px' }}
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>
                      Likelihood of Success
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            style={{
                              color: 'dimgrey',
                              marginLeft: '3px',
                              fontSize: '1em',
                              marginBottom: '5px',
                            }}
                          />
                        }
                        position="right center"
                        content="The estimated probability of reaching your target amount by your target date if you save the monthly amount shown."
                      />
                    </span>
                    <Header.Subheader style={{ margin: '.3rem auto .6rem' }}>
                      Without additional savings, the probability of reaching
                      your target wealth is{' '}
                      <span
                        style={{
                          margin: '0 0.2rem 0 0.2rem',
                          color: colors.navyBlue,
                          fontWeight: 'bold',
                          fontSize: '1.3rem',
                        }}
                      >
                        {`${Math.trunc(result.prob_without_savings * 100)} %`}
                      </span>
                      .
                      <br />
                      To improve the probability of reaching your goal, please
                      select a monthly savings target below:
                    </Header.Subheader>
                  </Header>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row columns={3} style={{ padding: 0 }}>
                {SAVING_OPTIONS.map((option) => {
                  const able = !!result[option.key];
                  const selected = monthlySavingChosen === option.value;

                  return (
                    <Grid.Column key={option.key}>
                      <OptionCard
                        selected={selected}
                        able={able}
                        borderColor={option.border}
                        onClick={() => {
                          if (!able) return;
                          this.updateMonthlySavingChosen(option.value);
                        }}
                      >
                        <Gauge
                          value={option.value}
                          label={option.label}
                          accent={option.accent}
                        />
                        <CardDivider />
                        <CardLabel>Required monthly savings</CardLabel>
                        <CardValue>
                          {`$${Number(result[option.key]).toLocaleString()}`}
                          <CardValueUnit>/mo</CardValueUnit>
                        </CardValue>
                        {able ? (
                          <DetailsButton selected={selected}>
                            <Icon name={selected ? 'check' : 'chart line'} />
                            {selected ? 'Selected' : 'Select'}
                          </DetailsButton>
                        ) : null}
                      </OptionCard>
                    </Grid.Column>
                  );
                })}
              </Grid.Row>
            </Grid>
          ) : (
            <Message
              visible
              warning
              style={{ maxWidth: '44rem', margin: '0 auto', marginTop: '2rem' }}
            >
              <Message.Header>
                It appears that you do not require additional savings for your
                retirement.
              </Message.Header>
            </Message>
          )}

          {(result['0.5'] === 0 ||
            result['0.75'] === 0 ||
            result['0.90'] === 0) &&
            requiredSavings && (
              <Message
                visible
                warning
                style={{
                  maxWidth: '44rem',
                  margin: '0 auto',
                  marginTop: '2rem',
                }}
              >
                <Message.Header>
                  For scenarios with $0, no additional savings are required.
                </Message.Header>
              </Message>
            )}

          {result &&
          monthlySavingChosen &&
          result[monthlySavingChosen] &&
          result.paths &&
          result.paths[monthlySavingChosen] ? (
            <ResultsContainer>
              {selectedValues.length && goalValues.length ? (
                <StatsRow>
                  <StatBoxGroup>
                    <StatBoxBorder borderColor={colors.red} />
                    <StatBox>
                      <StatBoxLabel>FUTURE VALUE OF GOAL</StatBoxLabel>
                      <StatBoxValue>
                        ${Math.ceil(futureValueOfGoal).toLocaleString()}
                        <RedDot />
                      </StatBoxValue>
                      <StatBoxSub>
                        In {yearsToTarget} years at 2% inflation
                      </StatBoxSub>
                    </StatBox>
                  </StatBoxGroup>

                  <StatBoxDivider>−</StatBoxDivider>

                  <StatBoxGroup>
                    <StatBoxBorder borderColor="#e6a817" />
                    <StatBox>
                      <StatBoxLabel>TOTAL CONTRIBUTIONS</StatBoxLabel>
                      <StatBoxValue>
                        ${Math.ceil(totalInvested).toLocaleString()}
                      </StatBoxValue>
                      <StatBoxSub>
                        ${startingAmount.toLocaleString()} starting + $
                        {Math.ceil(
                          totalInvested - startingAmount
                        ).toLocaleString()}{' '}
                        additional
                      </StatBoxSub>
                    </StatBox>
                  </StatBoxGroup>

                  <StatBoxDivider>=</StatBoxDivider>

                  <StatBoxGroup>
                    <StatBoxBorder borderColor={colors.navyBlue} />
                    <StatBox>
                      <StatBoxLabel>TOTAL RETURNS</StatBoxLabel>
                      <StatBoxValue>
                        ${Math.ceil(totalReturns).toLocaleString()}
                      </StatBoxValue>
                      <StatBoxSub>
                        <strong>
                          {Math.round(monthlySavingChosen * 100)}%
                        </strong>{' '}
                        of simulated total investment returns lie above $
                        {Math.ceil(totalReturns).toLocaleString()}
                      </StatBoxSub>
                    </StatBox>
                  </StatBoxGroup>
                </StatsRow>
              ) : null}

              <ResultsTitle>
                {'Simulated saving outcomes'.toUpperCase()}
              </ResultsTitle>
              <ChartSubtitle>
                <ChartSubtitleAccent>
                  {Math.round(monthlySavingChosen * 100)}%
                </ChartSubtitleAccent>{' '}
                of the final outcomes at target date lie above the future value
                of your goal <RedDot />
              </ChartSubtitle>
              {chartData && (
                <ChartWrapper>
                  <Line data={chartData} options={chartOptions} />
                </ChartWrapper>
              )}
            </ResultsContainer>
          ) : null}
          <div style={{ clear: 'both' }} />
        </BlueprintContent>
        {result &&
        monthlySavingChosen &&
        result[monthlySavingChosen] &&
        result.paths &&
        result.paths[monthlySavingChosen] ? (
          <TaxImplications
            isCouplePlan={isCouplePlan}
            tax_implications={result.tax_implications}
            result={result}
            history={history}
            dispatch={dispatch}
            match={match}
            monthlySavingChosen={monthlySavingChosen}
            isFetching={isFetching}
          />
        ) : null}
      </div>
    );
  }
}

Result.propTypes = {
  history: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object,
  isFetching: PropTypes.bool.isRequired,
  result: PropTypes.object,
  monthlySavingChosen: PropTypes.number,
  isCouplePlan: PropTypes.bool,
  startingAmount: PropTypes.number,
  yearsToTarget: PropTypes.number,
};

Result.defaultProps = {
  result: null,
  monthlySavingChosen: null,
  history: {},
  match: {},
  isCouplePlan: false,
  startingAmount: 0,
  yearsToTarget: 0,
};

export default connect(() => ({}))(Result);

const BlueprintContent = styled.div`
  margin: auto;
  padding: 2rem 3rem;
  text-align: left;
  background-color: white;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const OptionCard = styled.div`
  border: 1px solid
    ${(props) => (props.selected ? props.borderColor : '#e5e7eb')};
  background: ${(props) => (props.selected ? '#f4f3ff' : '#fff')};
  border-radius: 8px;
  padding: 1.5rem 1.25rem 1.25rem;
  text-align: center;
  cursor: ${(props) => (props.able ? 'pointer' : 'default')};
  box-shadow: ${(props) =>
    props.selected ? `0 0 0 1px ${props.borderColor}` : 'none'};
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: ${(props) => (props.able ? props.borderColor : '#e5e7eb')};
  }
`;

const GaugeWrapper = styled.div`
  position: relative;
  width: 204px;
  max-width: 100%;
  margin: 0 auto;
`;

const CardDivider = styled.div`
  height: 1px;
  margin: 1.25rem 0 1rem;
  background: rgba(0, 0, 0, 0.08);
`;

const CardLabel = styled.div`
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.6);
`;

const CardValue = styled.div`
  margin: 0.25rem 0 1rem;
  color: #1a1a1a;
  font-size: 2rem;
  font-weight: 700;
`;

const CardValueUnit = styled.span`
  margin-left: 2px;
  color: rgba(0, 0, 0, 0.5);
  font-size: 1rem;
  font-weight: 400;
`;

const DetailsButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.5rem 1.1rem;
  border: 1px solid ${(props) => (props.selected ? '#8e9df0' : '#d6d9de')};
  border-radius: 4px;
  background: ${(props) => (props.selected ? '#e8ebff' : '#fff')};
  color: rgba(0, 0, 0, 0.75);
  font-size: 0.95rem;

  i.icon {
    margin: 0 4px 0 0;
    color: rgba(0, 0, 0, 0.5);
  }
`;

const ResultsContainer = styled.div`
  margin-top: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.4);
  border-radius: 8px;
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const StatBoxGroup = styled.div`
  display: flex;
  flex: 1;
  min-width: 183px;
  max-width: 263px;
`;

const StatBox = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 1.25rem;
  text-align: left;
`;

const StatBoxBorder = styled.div`
  width: 0;
  height: 90px;
  border-left: 3px solid ${(props) => props.borderColor || '#ccc'};
  align-self: center;
`;

const StatBoxLabel = styled.div`
  margin-bottom: 0.3rem;
  color: rgba(0, 0, 0, 0.45);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const StatBoxValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 0.25rem;
  color: #1a1a1a;
  font-size: 1.6rem;
  font-weight: 700;
`;

const StatBoxSub = styled.div`
  color: black;
  font-size: 1rem;
`;

const StatBoxDivider = styled.div`
  flex-shrink: 0;
  align-self: center;
  margin-bottom: 1rem;
  padding: 0 0.75rem;
  color: rgba(0, 0, 0, 0.3);
  font-size: 2rem;
`;

const ResultsTitle = styled.h2`
  margin: 0 auto 0.25rem;
  color: #1a1a1a;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
`;

const ChartSubtitle = styled.p`
  margin: 0 auto 1rem;
  color: rgba(0, 0, 0, 0.65);
  font-size: 0.95rem;
  text-align: center;
`;

const ChartSubtitleAccent = styled.span`
  color: ${colors.blue};
  font-weight: 700;
`;

const RedDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-left: 2px;
  border-radius: 50%;
  background-color: #e53935;
  vertical-align: middle;
`;

const ChartWrapper = styled.div`
  width: 80%;
  margin: auto;
`;
